import { FormikProvider, useFormik } from 'formik';
import { ChangeEvent, useCallback } from 'react';
import * as yup from 'yup';
import RegisterClassesSelectPaginate, {
  ClassItemOption,
} from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import {
  NINE_DIGITS_WITH_SEPARATOR_REGEX,
  SIX_DIGITS_WITH_SEPARATOR_REGEX,
} from '@/modules/common/utils/regex.ts';
import {
  useIssuerAddBankMutation,
  useIssuerUpdateBankMutation,
} from '@/modules/issuer-dashboard/queries/banks.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectPaginateOption,
  TextInput,
  TooltipNew,
} from 'ui';
import { fixNameEnum } from 'ui/utils';
import { BankTypeEnum, CreateRegisterBankRequest, CurrencyEnum, RegisterBankExtended } from 'api';

const validationSchema = yup.object<CreateRegisterBankRequest>({
  id: yup.number().nullable().optional(),
  bank_account_name: yup
    .string()
    .trim()
    .required('This field is required')
    .max(40, 'Maximum length 40'),
  bank_account_bsb: yup
    .string()
    .matches(
      SIX_DIGITS_WITH_SEPARATOR_REGEX,
      'Invalid BSB, must be 6 digits with optional dash or space',
    )
    .required('This field is required'),
  bank_account_number: yup
    .string()
    .matches(
      NINE_DIGITS_WITH_SEPARATOR_REGEX,
      'Invalid account number, must be 8 or 9 digits with optional dash or space',
    )
    .required('This field is required'),
  bank_name: yup
    .string()
    .required('This field is required')
    .uppercase('Must be large characters')
    .length(3, 'Length must be 3 characters'),
  bank_types: yup
    .array()
    .of(
      yup
        .mixed<BankTypeEnum>()
        .oneOf(Object.values(BankTypeEnum))
        .required('This field is required'),
    )
    .required('This field is required'),
  bank_currency: yup
    .mixed<CurrencyEnum>()
    .oneOf(Object.values(CurrencyEnum))
    .required('This field is required'),
  bank_classes: yup.array().of(yup.number()).nullable().optional(),
});

export interface IssuerAddUpdateBankModalProps extends BaseModalProps {
  item?: RegisterBankExtended;
  portfolioId?: number;
}

export const IssuerAddUpdateBankModal = ({
  open,
  onClose,
  item,
  portfolioId,
}: IssuerAddUpdateBankModalProps) => {
  const formik = useFormik<CreateRegisterBankRequest>({
    validationSchema,
    initialValues: {
      bank_account_name: item?.bank_account_name || '',
      bank_currency: item?.bank_currency || ('' as CurrencyEnum),
      bank_name: item?.bank_name || '',
      bank_classes: item?.bank_classes || [],
      bank_account_bsb: item?.bank_account_bsb || '',
      bank_account_number: item?.bank_account_number || '',
      bank_types: item?.bank_types || [],
      portfolio_id: portfolioId,
    },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: values => {
      if (item) {
        mutateAsyncUpdate({
          ...values,
          id: item.id,
        });
      } else {
        mutateAsyncAdd(values);
      }
    },
  });

  const { mutateAsync: mutateAsyncAdd, isLoading: isLoadingAdd } = useIssuerAddBankMutation({
    setErrors: formik.setErrors,
    onSuccess: onClose,
  });
  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    useIssuerUpdateBankMutation({
      setErrors: formik.setErrors,
      onSuccess: onClose,
    });

  const handleChangeSelect = useCallback(
    (field: string) => (option: string | boolean | null | any) => {
      formik.setFieldValue(field, option);
      formik.setFieldError(field, '');
    },
    [formik],
  );

  const handleChangeInputBank = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value;

      handleChangeSelect(name)(value);
    },
    [handleChangeSelect],
  );

  const handleChangeRegisterClass = useCallback(
    (fieldName: string) => (options: SelectPaginateOption<ClassItemOption>[]) => {
      const register_class_ids = options.map(v => v.value.id);
      handleChangeSelect(fieldName)(register_class_ids);
    },
    [handleChangeSelect],
  );

  return (
    <ModalContentWrapper
      onClose={onClose}
      open={open}
      title={item ? 'Update bank' : 'New bank'}
      classes={{ children: 'p-0' }}
    >
      <FormikProvider value={formik}>
        <div className="p-3 sm:p-6 flex flex-col w-full gap-6">
          <TextInput
            name="bank_account_name"
            placeholder="Your entity name"
            label="bank account name"
            onChange={handleChangeInputBank}
            value={formik.values.bank_account_name}
            error={formik.errors.bank_account_name}
          />

          <Select
            value={formik.values.bank_types}
            placeholder="Select bank types"
            label="Bank types"
            isMulti={true}
            onChange={handleChangeSelect('bank_types')}
            options={Object.entries(BankTypeEnum).map(item => ({
              label: (
                <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                  <span className="text-gray-900">{fixNameEnum(item[0])}</span>
                </span>
              ),
              value: item[1],
              toString: () => fixNameEnum(item[0]),
            }))}
            error={String(formik.errors.bank_types || '') as string}
          />

          <RegisterClassesSelectPaginate
            isMulti={true}
            label="CLASSES"
            onChange={handleChangeRegisterClass('bank_classes')}
            value={formik.values.bank_classes}
            error={String(formik.errors.bank_classes || '') as string}
            showAddNewItem={true}
            showPriceInLabel={false}
          />

          <div className="flex flex-col gap-1">
            <TextInput
              name="bank_name"
              label="Bank name"
              placeholder="CBA"
              onChange={handleChangeInputBank}
              value={formik.values.bank_name}
              error={formik.errors.bank_name}
            />
            <span className="text-xs leading-5 text-gray-600">
              Must be approved Financial Institution abbreviation. For example, Bank of Queensland's
              abbreviation is BQL, Westpac's abbreviation is "WBC". Consult your Bank for correct
              abbreviation.
            </span>
          </div>

          <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
            <TextInput
              name="bank_account_number"
              placeholder="1111 2222 4"
              label="bank account number"
              onChange={handleChangeInputBank}
              value={formik.values.bank_account_number}
              error={formik.errors.bank_account_number}
            />
          </div>
          <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
            <TextInput
              name="bank_account_bsb"
              placeholder="123-123"
              label="BSB"
              onChange={handleChangeInputBank}
              value={formik.values.bank_account_bsb}
              error={formik.errors.bank_account_bsb}
            />
            <TooltipNew
              text="Сurrency can only be selected when creating a bank"
              disabled={!item}
              className="w-full"
            >
              <Select<CurrencyEnum>
                value={formik.values.bank_currency}
                placeholder="Select currency"
                label="Currency"
                positionMenu="up"
                onChange={handleChangeSelect('bank_currency')}
                disabled={!!item}
                options={Object.entries(CurrencyEnum).map(item => ({
                  label: (
                    <span className="flex gap-4 items-center font-[Inter] text-sm leading-5">
                      <span className="text-gray-900">{item[1].toUpperCase()}</span>
                      {/* <span className="text-gray-500">{item[0].toUpperCase()}</span> */}
                    </span>
                  ),
                  value: item[1],
                  toString: () => fixNameEnum(item[0]),
                }))}
                error={String(formik.errors.bank_currency || '') as string}
              />
            </TooltipNew>
          </div>
        </div>
        <div className="mt-4 p-3 !pb-0 sm:p-6">
          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={isLoadingAdd || isLoadingUpdate}
          >
            {item ? 'Update bank information' : 'Add new bank'}
          </Button>
        </div>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
